"use client";

import { useId, useRef, useState, useTransition } from "react";
import { Send } from "lucide-react";
import { ActionButton } from "@/features/home-v2/form/Action";
import { Dialog, toast } from "@/features/home-v2/ui";
import { sendMessage } from "@/features/message/api";
import { useAuthStore } from "@/store/useAuthStore";
import { resolveToastMessage } from "@/lib/api-toast";
type Props = {
  open: boolean;
  onClose: () => void;
  lang: "fa" | "en";
  targetId: number;
  name: string;
};

/** Message an artist from their profile. Replaces the legacy `MessageArtistModal`,
 *  whose only caller this page was. */
export default function ArtistMessageDialog({
  open,
  onClose,
  lang,
  targetId,
  name
}: Props) {
  const isFa = lang === "fa";
  const fieldId = useId();
  const {
    user,
    openLoginModal
  } = useAuthStore();
  const [message, setMessage] = useState("");
  const [isPending, startTransition] = useTransition();
  // The in-flight latch: a double press sends the message once.
  const inFlight = useRef(false);
  const close = () => {
    if (inFlight.current) return;
    setMessage("");
    onClose();
  };
  const send = () => {
    const text = message.trim();
    if (!text || inFlight.current) return;
    if (!user) {
      openLoginModal();
      return;
    }
    inFlight.current = true;
    startTransition(async () => {
      try {
        await sendMessage({
          target_id: targetId,
          text
        });
        toast({
          title: isFa ? "پیام ارسال شد" : "Message sent",
          tone: "success"
        });
        inFlight.current = false;
        close();
      } catch (error) {
        // Null when the interceptor already told the user why — one message
        // per failure, and it is the server's (lib/api-toast.ts).
        const text_0 = resolveToastMessage(error, isFa ? "ارسال پیام ناموفق بود" : "Failed to send message");
        if (text_0) toast({
          title: text_0,
          tone: "danger"
        });
      } finally {
        inFlight.current = false;
      }
    });
  };
  return <Dialog open={open} onClose={close} title={isFa ? `پیام به ${name}` : `Message ${name}`} closeLabel={isFa ? "بستن" : "Close"} busy={isPending} footer={<>
          <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={close} disabled={isPending}>
            {isFa ? "انصراف" : "Cancel"}
          </button>
          <ActionButton onClick={send} disabled={!message.trim()} busy={isPending} icon={<Send aria-hidden />} busyLabel={isFa ? "در حال ارسال…" : "Sending…"}>
            {isFa ? "ارسال" : "Send"}
          </ActionButton>
        </>}>
      <label className="v2-field" htmlFor={fieldId}>
        <span>{isFa ? "متن پیام" : "Your message"}</span>
        <textarea id={fieldId} className="v2-input v2-profile-message-input" rows={5} value={message} onChange={event => setMessage(event.target.value)} placeholder={isFa ? "پیام خود را اینجا بنویسید..." : "Write your message here..."} disabled={isPending} />
      </label>
    </Dialog>;
}